/**
 * Data layer for the admin section. Kept apart from `queries.ts` (which every signed-in user
 * loads) so the admin surface stays in one place.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ActivityAdmin,
  ActivityList,
  AuditLogList,
  CreateActivityInput,
  CreateGroupInput,
  CreateProjectInput,
  CreateReportInput,
  CreateUserInput,
  GroupDetail,
  GroupList,
  ImportUsersResult,
  ListActivitiesQuery,
  ListAuditLogsQuery,
  ListGroupsQuery,
  ListProjectsQuery,
  ListReportsQuery,
  ListUsersQuery,
  MergeResult,
  NotificationList,
  OrgDashboard,
  OrgDashboardQuery,
  OrganizationSettings,
  ProjectAdmin,
  ProjectList,
  ImportWorkbookResult,
  MaintenanceStats,
  ReportAccepted,
  ReportList,
  ResetRequest,
  ResetResult,
  ResetUserPasswordResult,
  UpdateActivityInput,
  UpdateGroupInput,
  UpdateOrganizationInput,
  UpdateProjectInput,
  UpdateUserInput,
  UserAdmin,
  UserList,
} from '@tt/shared';
import { api, API_BASE } from './api';

export const adminKeys = {
  users: (query: Partial<ListUsersQuery>) => ['admin-users', query] as const,
  user: (id: string) => ['admin-user', id] as const,
  groups: (query: Partial<ListGroupsQuery>) => ['admin-groups', query] as const,
  group: (id: string) => ['admin-group', id] as const,
  projects: (query: Partial<ListProjectsQuery>) => ['admin-projects', query] as const,
  activities: (query: Partial<ListActivitiesQuery>) => ['admin-activities', query] as const,
  auditLogs: (query: Partial<ListAuditLogsQuery>) => ['admin-audit-logs', query] as const,
  dashboard: (query: OrgDashboardQuery) => ['admin-dashboard', query] as const,
  organization: ['admin-organization'] as const,
};

/** Anything that changes who may book what invalidates the lists and the pickers that read them. */
function invalidateCatalog(qc: QueryClient) {
  return Promise.all(
    [
      'admin-users',
      'admin-user',
      'admin-groups',
      'admin-group',
      'admin-projects',
      'admin-activities',
      'admin-dashboard',
      'options',
      'week',
      'month',
      'entries',
      'dashboard',
    ].map((key) => qc.invalidateQueries({ queryKey: [key] })),
  );
}

// ------------------------------------------------------------------- users

export const useAdminUsers = (query: Partial<ListUsersQuery> & { enabled?: boolean } = {}) => {
  const { enabled = true, ...params } = query;
  return useQuery({
    queryKey: adminKeys.users(params),
    queryFn: () => api<UserList>('/admin/users', { query: { ...params, sort: undefined } }),
    enabled,
    staleTime: 30_000,
  });
};

export const useAdminUser = (id: string | undefined) =>
  useQuery({
    queryKey: adminKeys.user(id ?? ''),
    queryFn: () => api<UserAdmin>(`/admin/users/${id}`),
    enabled: Boolean(id),
  });

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api<UserAdmin>('/admin/users', { method: 'POST', body: input }),
    onSuccess: () => invalidateCatalog(qc),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateUserInput & { id: string }) =>
      api<UserAdmin>(`/admin/users/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => invalidateCatalog(qc),
  });
}

export function useUserAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string;
      action: 'activate' | 'deactivate' | 'resend-invite' | 'delete' | 'anonymize';
    }): Promise<void> => {
      if (action === 'delete') return api<void>(`/admin/users/${id}`, { method: 'DELETE' });
      // The updated row comes back from the status/anonymize calls but the lists refetch anyway.
      if (action === 'anonymize') {
        await api<UserAdmin>(`/admin/users/${id}/anonymize`, {
          method: 'POST',
          body: { confirm: 'ANONYMIZE' },
        });
        return;
      }
      if (action === 'resend-invite')
        return api<void>(`/admin/users/${id}/resend-invite`, { method: 'POST' });
      await api<UserAdmin>(`/admin/users/${id}/status`, {
        method: 'PATCH',
        body: { status: action === 'activate' ? 'ACTIVE' : 'INACTIVE' },
      });
    },
    onSuccess: () => invalidateCatalog(qc),
  });
}

/**
 * Resets a password and returns the temporary one. Nothing caches the result: it is shown once,
 * in the dialog that opened it, and asking again produces a different password.
 */
export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<ResetUserPasswordResult>(`/admin/users/${id}/reset-password`, { method: 'POST' }),
    // Deliberately not returning the invalidation: every other mutation here waits for the lists
    // to refetch before it resolves, which is right when the result *is* the refreshed list. This
    // one's result is a password the administrator has to read out, and making them wait seconds
    // for an unrelated refetch before it appears is how a one-time secret gets missed.
    onSuccess: () => {
      void invalidateCatalog(qc);
    },
  });
}

/** Posts the raw CSV; `dryRun` reports what a real import would do without writing. */
export function useImportUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ csv, dryRun }: { csv: string; dryRun: boolean }) => {
      const res = await fetch(`${API_BASE}/admin/users/import?dryRun=${dryRun}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'fetch',
          'Content-Type': 'text/csv',
          Accept: 'application/json',
        },
        body: csv,
      });
      const json: unknown = await res.json();
      if (!res.ok) throw json;
      return json as ImportUsersResult;
    },
    onSuccess: (result) => (result.dryRun ? Promise.resolve() : invalidateCatalog(qc)),
  });
}

/** The export is a download, not a query: the browser saves the JSON the endpoint returns. */
export function downloadUserData(id: string, name: string) {
  const link = document.createElement('a');
  link.href = `${API_BASE}/admin/users/${id}/export`;
  link.download = `${name.replace(/\s+/g, '-').toLowerCase()}-personal-data.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// ------------------------------------------------------------------ maintenance

export const useMaintenanceStats = () =>
  useQuery({
    queryKey: ['admin-maintenance'],
    queryFn: () => api<MaintenanceStats>('/admin/maintenance/stats'),
  });

/** A plain link, so the browser downloads the workbook with the filename the API chose. */
export function downloadWorkbook() {
  const link = document.createElement('a');
  link.href = `${API_BASE}/admin/maintenance/export`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function useImportWorkbook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) =>
      api<ImportWorkbookResult>(`/admin/maintenance/import?dryRun=${String(dryRun)}`, {
        method: 'POST',
        rawBody: file,
        contentType: file.type || 'application/octet-stream',
      }),
    onSuccess: (result) => (result.dryRun ? Promise.resolve() : invalidateCatalog(qc)),
  });
}

export function useResetOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ResetRequest) =>
      api<ResetResult>('/admin/maintenance/reset', { method: 'POST', body: input }),
    onSuccess: () => invalidateCatalog(qc),
  });
}

// ------------------------------------------------------------------ groups

export const useAdminGroups = (query: Partial<ListGroupsQuery> = {}) =>
  useQuery({
    queryKey: adminKeys.groups(query),
    queryFn: () => api<GroupList>('/admin/groups', { query: { ...query, sort: undefined } }),
    staleTime: 30_000,
  });

export const useAdminGroup = (id: string | undefined) =>
  useQuery({
    queryKey: adminKeys.group(id ?? ''),
    queryFn: () => api<GroupDetail>(`/admin/groups/${id}`),
    enabled: Boolean(id),
  });

export function useGroupMutations() {
  const qc = useQueryClient();
  const done = () => invalidateCatalog(qc);
  return {
    create: useMutation({
      mutationFn: (input: CreateGroupInput) =>
        api<GroupDetail>('/admin/groups', { method: 'POST', body: input }),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: UpdateGroupInput & { id: string }) =>
        api<GroupDetail>(`/admin/groups/${id}`, { method: 'PATCH', body: input }),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
        api<void>(`/admin/groups/${id}`, { method: 'DELETE', query: { force } }),
      onSuccess: done,
    }),
    addMembers: useMutation({
      mutationFn: ({ id, ids }: { id: string; ids: string[] }) =>
        api<{ added: number }>(`/admin/groups/${id}/members`, { method: 'POST', body: { ids } }),
      onSuccess: done,
    }),
    removeMember: useMutation({
      mutationFn: ({ id, userId }: { id: string; userId: string }) =>
        api<void>(`/admin/groups/${id}/members/${userId}`, { method: 'DELETE' }),
      onSuccess: done,
    }),
    setProjects: useMutation({
      mutationFn: ({ id, ids }: { id: string; ids: string[] }) =>
        api<GroupDetail>(`/admin/groups/${id}/projects`, { method: 'PUT', body: { ids } }),
      onSuccess: done,
    }),
    setActivities: useMutation({
      mutationFn: ({ id, ids }: { id: string; ids: string[] }) =>
        api<GroupDetail>(`/admin/groups/${id}/activities`, { method: 'PUT', body: { ids } }),
      onSuccess: done,
    }),
  };
}

// ---------------------------------------------------------------- projects

export const useAdminProjects = (query: Partial<ListProjectsQuery> = {}) =>
  useQuery({
    queryKey: adminKeys.projects(query),
    queryFn: () => api<ProjectList>('/admin/projects', { query: { ...query, sort: undefined } }),
    staleTime: 30_000,
  });

export function useProjectMutations() {
  const qc = useQueryClient();
  const done = () => invalidateCatalog(qc);
  return {
    create: useMutation({
      mutationFn: (input: CreateProjectInput) =>
        api<ProjectAdmin>('/admin/projects', { method: 'POST', body: input }),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: UpdateProjectInput & { id: string }) =>
        api<ProjectAdmin>(`/admin/projects/${id}`, { method: 'PATCH', body: input }),
      onSuccess: done,
    }),
    setArchived: useMutation({
      mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
        api<ProjectAdmin>(`/admin/projects/${id}/${archived ? 'archive' : 'unarchive'}`, {
          method: 'POST',
        }),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api<void>(`/admin/projects/${id}`, { method: 'DELETE' }),
      onSuccess: done,
    }),
    setGroups: useMutation({
      mutationFn: ({ id, ids }: { id: string; ids: string[] }) =>
        api<ProjectAdmin>(`/admin/projects/${id}/groups`, { method: 'PUT', body: { ids } }),
      onSuccess: done,
    }),
  };
}

// -------------------------------------------------------------- activities

export const useAdminActivities = (query: Partial<ListActivitiesQuery> = {}) =>
  useQuery({
    queryKey: adminKeys.activities(query),
    queryFn: () => api<ActivityList>('/admin/activities', { query: { ...query, sort: undefined } }),
    staleTime: 30_000,
  });

export function useActivityMutations() {
  const qc = useQueryClient();
  const done = () => invalidateCatalog(qc);
  return {
    create: useMutation({
      mutationFn: (input: CreateActivityInput) =>
        api<ActivityAdmin>('/admin/activities', { method: 'POST', body: input }),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: UpdateActivityInput & { id: string }) =>
        api<ActivityAdmin>(`/admin/activities/${id}`, { method: 'PATCH', body: input }),
      onSuccess: done,
    }),
    setArchived: useMutation({
      mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
        api<ActivityAdmin>(`/admin/activities/${id}/${archived ? 'archive' : 'unarchive'}`, {
          method: 'POST',
        }),
      onSuccess: done,
    }),
    promote: useMutation({
      mutationFn: ({ id, groupIds }: { id: string; groupIds: string[] }) =>
        api<ActivityAdmin>(`/admin/activities/${id}/promote`, {
          method: 'POST',
          body: { groupIds },
        }),
      onSuccess: done,
    }),
    merge: useMutation({
      mutationFn: ({ id, targetActivityId }: { id: string; targetActivityId: string }) =>
        api<MergeResult>(`/admin/activities/${id}/merge`, {
          method: 'POST',
          body: { targetActivityId },
        }),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api<void>(`/admin/activities/${id}`, { method: 'DELETE' }),
      onSuccess: done,
    }),
    setGroups: useMutation({
      mutationFn: ({ id, ids }: { id: string; ids: string[] }) =>
        api<ActivityAdmin>(`/admin/activities/${id}/groups`, { method: 'PUT', body: { ids } }),
      onSuccess: done,
    }),
  };
}

// ------------------------------------------------- audit log, dashboard, org

export const useAuditLogs = (query: Partial<ListAuditLogsQuery> = {}) =>
  useQuery({
    queryKey: adminKeys.auditLogs(query),
    queryFn: () => api<AuditLogList>('/admin/audit-logs', { query }),
  });

export const useOrgDashboard = (query: OrgDashboardQuery) =>
  useQuery({
    queryKey: adminKeys.dashboard(query),
    queryFn: () => api<OrgDashboard>('/admin/dashboard', { query }),
    staleTime: 30_000,
  });

export const useOrganization = () =>
  useQuery({
    queryKey: adminKeys.organization,
    queryFn: () => api<OrganizationSettings>('/admin/organization'),
  });

export function useUpdateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOrganizationInput) =>
      api<OrganizationSettings>('/admin/organization', { method: 'PATCH', body: input }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: adminKeys.organization }),
        // Week start and hours format change how every time view renders.
        qc.invalidateQueries({ queryKey: ['me'] }),
        qc.invalidateQueries({ queryKey: ['week'] }),
        qc.invalidateQueries({ queryKey: ['month'] }),
      ]),
  });
}

/**
 * The logo goes up as the raw file, the way the workbook import does — one file, nothing to
 * encode, and the server reads the format from the bytes rather than from what we claim here.
 */
export function useUploadOrganizationLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      api<OrganizationSettings>('/admin/organization/logo', {
        method: 'POST',
        rawBody: file,
        contentType: file.type,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.organization }),
  });
}

export function useRemoveOrganizationLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api('/admin/organization/logo', { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.organization }),
  });
}

// ----------------------------------------------------------------- reports

export const reportKeys = {
  list: (query: Partial<ListReportsQuery>) => ['admin-reports', query] as const,
  notifications: ['notifications'] as const,
};

/**
 * Report history. While anything is still queued or running the list refetches every few
 * seconds, which is also what surfaces the completion notification.
 */
export const useReports = (query: Partial<ListReportsQuery> = {}) =>
  useQuery({
    queryKey: reportKeys.list(query),
    queryFn: () => api<ReportList>('/admin/reports', { query }),
    refetchInterval: (q) =>
      q.state.data?.data.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING')
        ? 2_000
        : false,
  });

export function useRequestReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      kind,
      ...body
    }: { kind: 'pdf' | 'entries' | 'database' } & Partial<CreateReportInput>) => {
      const path =
        kind === 'pdf'
          ? '/admin/reports'
          : kind === 'entries'
            ? '/admin/exports/entries'
            : '/admin/exports/database';
      return api<ReportAccepted>(path, { method: 'POST', body });
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['admin-reports'] }),
        qc.invalidateQueries({ queryKey: reportKeys.notifications }),
      ]),
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/admin/reports/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reports'] }),
  });
}

/** Follows the 302 to the signed link, so the file is fetched without the session cookie. */
export function downloadReport(id: string) {
  window.location.assign(`${API_BASE}/admin/reports/${id}/download`);
}

export const useNotifications = (enabled = true) =>
  useQuery({
    queryKey: reportKeys.notifications,
    queryFn: () => api<NotificationList>('/me/notifications'),
    enabled,
    refetchInterval: 15_000,
  });

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) =>
      api<void>('/me/notifications/read', { method: 'POST', body: { ids } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: reportKeys.notifications }),
  });
}
